import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCheckoutStatus } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Check, ArrowRight, AlertCircle, Loader2 } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 8;

export default function CheckoutSuccess() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling");
    const [summary, setSummary] = useState(null);
    const { clear } = useCart();
    const clearedRef = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setStatus("failed");
            return;
        }
        let cancelled = false;
        let attempts = 0;

        const poll = async () => {
            attempts += 1;
            try {
                const data = await getCheckoutStatus(sessionId);
                if (cancelled) return;
                setSummary(data);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    if (!clearedRef.current) {
                        clearedRef.current = true;
                        clear();
                    }
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                if (attempts >= MAX_POLL_ATTEMPTS) {
                    setStatus("failed");
                    return;
                }
                setTimeout(poll, POLL_INTERVAL_MS);
            } catch (e) {
                if (!cancelled) setStatus("failed");
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [sessionId, clear]);

    return (
        <div data-testid="checkout-success-page" className="pt-32 min-h-[80vh]">
            <section className="container-rail max-w-3xl mx-auto py-16 text-center space-y-8">
                {status === "polling" && (
                    <>
                        <Loader2 className="h-10 w-10 mx-auto animate-spin text-[#A3A3A3]" strokeWidth={1.2} />
                        <div className="text-overline text-[#A3A3A3]">{t("checkout_success.polling_label")}</div>
                        <h1 className="text-display text-4xl sm:text-5xl">{t("checkout_success.polling_title")}</h1>
                        <p className="text-[#A3A3A3]">{t("checkout_success.polling_body")}</p>
                    </>
                )}
                {status === "paid" && summary && (
                    <>
                        <div className="inline-flex h-14 w-14 items-center justify-center border border-white/15 mx-auto">
                            <Check className="h-6 w-6 text-[#F4F4F0]" strokeWidth={1.3} />
                        </div>
                        <div className="text-overline text-[#A3A3A3]" data-testid="success-overline">{t("checkout_success.overline")}</div>
                        <h1 className="text-display text-5xl sm:text-6xl leading-tight">
                            {t("checkout_success.title_1")} <br />
                            <span className="text-display-italic text-[#A3A3A3]">{t("checkout_success.title_italic")}</span>
                        </h1>
                        <p className="text-[#A3A3A3] max-w-md mx-auto">
                            {t("checkout_success.body")}
                        </p>

                        <div className="border border-white/10 text-left p-6 sm:p-8 max-w-xl mx-auto" data-testid="order-summary">
                            <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-4">
                                <div className="text-overline text-[#A3A3A3]">{t("checkout_success.summary_label")}</div>
                                <div className="text-overline text-[#666]">{summary.currency?.toUpperCase()}</div>
                            </div>
                            <ul className="divide-y divide-white/[0.06]">
                                {summary.items?.map((it, i) => (
                                    <li key={`${it.product_id}-${it.size}-${i}`} className="py-3 flex items-center gap-4">
                                        {it.image && (
                                            <div className="w-14 h-16 shrink-0 bg-[#141414] overflow-hidden border border-white/[0.06]">
                                                <img src={it.image} alt={it.name} className="h-full w-full object-cover" />
                                            </div>
                                        )}
                                        <div className="flex-1 text-left">
                                            <div className="text-display text-lg leading-tight">{it.name}</div>
                                            <div className="text-[11px] tracking-[0.18em] uppercase text-[#A3A3A3] mt-0.5 font-display">
                                                {t("checkout_success.size")} {it.size} · {t("checkout_success.qty")} {it.qty}
                                            </div>
                                        </div>
                                        <div className="text-brutal text-sm">${(it.price * it.qty).toFixed(0)}</div>
                                    </li>
                                ))}
                            </ul>
                            <div className="border-t border-white/10 mt-4 pt-4 flex items-center justify-between">
                                <span className="text-overline text-[#A3A3A3]">{t("checkout_success.paid")}</span>
                                <span className="text-display text-2xl" data-testid="paid-total">
                                    ${summary.amount_total?.toFixed(0)}
                                </span>
                            </div>
                        </div>

                        <Link to="/shop" data-testid="success-continue" className="btn-primary inline-flex">
                            {t("checkout_success.continue")} <ArrowRight className="h-4 w-4" />
                        </Link>
                    </>
                )}
                {(status === "failed" || status === "expired") && (
                    <>
                        <AlertCircle className="h-10 w-10 mx-auto text-[#591C1C]" strokeWidth={1.2} />
                        <div className="text-overline text-[#591C1C]">{t("checkout_success.fail_label")}</div>
                        <h1 className="text-display text-4xl sm:text-5xl">{t("checkout_success.fail_title")}</h1>
                        <p className="text-[#A3A3A3] max-w-md mx-auto">
                            {t("checkout_success.fail_body")}
                        </p>
                        <Link to="/shop" className="btn-ghost inline-flex">{t("checkout_success.return_shop")}</Link>
                    </>
                )}
            </section>
        </div>
    );
}
